import React from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Typography,
  Paper,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import { Link } from "react-router-dom";
import { Branch } from "../../../interfaces/entities/branch";
import { Company } from "../../../interfaces/entities/company";
import {
  ProfileSection,
  AvatarWrapper,
  BranchNameRow,
  BranchName,
  IconWrapper,
  InfoLabel
} from "./branchView.styles";
import { Collection } from "../../../enums/collections.enum";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";

interface BranchViewProps {
  branch: Branch;
  company: Company;
  onQrCodeClick?: () => void;
  onDelete?: () => void;
}

export const BranchViewPresentational: React.FC<BranchViewProps> = ({ branch, company, onQrCodeClick, onDelete }) => {

  const { currentUserIsOwnerOf } = useAuthenticatedUser();

  return (
    <Box sx={{ margin: 2 }}>
      {currentUserIsOwnerOf(company.id) ? <Paper
        elevation={2}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          padding: 3,
          mb: 3,
          backgroundImage: `url(${branch?.image || ""})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.90)', zIndex: 0 }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <ProfileSection>
            <AvatarWrapper
              src={company?.image || "/assets/default-user.jpg"}
              alt={company.name}
            />
            <Box>
              <BranchNameRow sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                mt: { xs: 2, md: 0 }
              }}>
                <BranchName variant="h5">
                  {branch?.name || "N/A"}
                </BranchName>

                <Box>
                  <Tooltip title="Generate QR">
                    <IconButton
                      onClick={onQrCodeClick}
                      sx={{
                        borderRadius: 2,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <QrCodeIcon />
                    </IconButton>
                  </Tooltip>
                  {currentUserIsOwnerOf(company.id) && <Tooltip title="Delete Branch">
                    <IconButton
                      onClick={onDelete}
                      color="error"
                      sx={{
                        borderRadius: 2,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>}
                  {currentUserIsOwnerOf(company.id) && <Tooltip title="Edit Branch">
                    <IconButton
                      component={Link}
                      to={`/${Collection.Companies}/${branch.companyId}/${Collection.Branches}/${branch.id}/edit`}
                      sx={{
                        borderRadius: 2,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>}
                </Box>
              </BranchNameRow>
              <Typography variant="body2" color="text.secondary">
                {branch.address}
              </Typography>
              {branch.urlKey ? <a href={`${window.location.origin}/${branch.urlKey}`} target="_blank" rel="noopener noreferrer" style={{ color: 'text.secondary' }}>
                {`${window.location.origin}/${branch.urlKey}`}
              </a> : null}
            </Box>
          </ProfileSection>

          <Divider sx={{ my: 2 }} />

          <Box>
            <IconWrapper>
              <LocationOnIcon color="action" />
              <Typography>
                <InfoLabel>Address:</InfoLabel> {branch.address || "Not available"}
              </Typography>
            </IconWrapper>

            <IconWrapper>
              <PhoneIcon color="action" />
              <Typography>
                <InfoLabel>Contact:</InfoLabel> {branch.phone || "Not available"}
              </Typography>
            </IconWrapper>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              component={Link}
              to={`/${Collection.Companies}/${branch.companyId}/${Collection.Branches}/${branch.id}/${Collection.Reviews}`}
              variant="contained"
              size="large"
              color="warning"
            >
              View Reviews
            </Button>
          </Box>
        </Box>
      </Paper> : <>
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            overflow: 'visible',
            textAlign: "center",
            mb: 6,
            borderRadius: 3,
            backgroundImage: `url(${branch.image || ""})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            border: "1px solid #e6ecff",
            height: 180,
          }}
        >
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.2)', zIndex: 0, borderRadius: 3 }} />
          <Box
            component="img"
            src={company.image || "/assets/default-user.jpg"}
            alt={company.name}
            sx={{
              position: 'absolute',
              bottom: -48,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 96,
              height: 96,
              borderRadius: '50%',
              objectFit: 'cover',
              border: '4px solid white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              zIndex: 1,
            }}
          />
        </Paper>

        <Box sx={{ textAlign: "center", px: 2 }}>
          <Typography variant="subtitle1" sx={{ color: "text.secondary", mb: 1 }}>
            {company.name} - {branch.name || "Welcome"}
          </Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            Rate Your Experience
          </Typography>
            <Typography>
            ⭐ ⭐ ⭐ ⭐ 
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: 320, mx: "auto", color: "text.secondary" }}>
            Help us improve by reviewing our staff.
            Your feedback helps us recognize great service.
          </Typography>
        </Box>
      </>}
    </Box>
  );
};
